import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { ApiUrlHelper } from '../config/api-url-helper';
import { ONBOARDING_PATHS, type OnboardingPath } from '../constants/onboarding-routes';
import { readApiString } from '../utils/api-response.util';
// NEW (disabled, API-side flag version) needs: import { readApiBool, readApiString } from '../utils/api-response.util';
import { withQueryParams } from '../utils/api-url.util';
import { AppService } from './app.service';
import { OnboardingStateService } from './onboarding-state.service';
import { StorageService } from './storage.service';

@Injectable({ providedIn: 'root' })
export class OnboardingRouteService {
  private readonly onboardingState = inject(OnboardingStateService);
  private readonly storage = inject(StorageService);
  private readonly router = inject(Router);
  private readonly appService = inject(AppService);
  private readonly apiUrl = inject(ApiUrlHelper);

  private readonly stepKey = 'cremello_onboarding_step';
  private readonly returnStepKey = 'cremello_onboarding_return_step';

  readonly currentStep = signal<OnboardingPath>(ONBOARDING_PATHS.accountType);

  async ensureEncryptedRef(applicationNumber: string): Promise<string> {
    let ref = '';

    try {
      const response = await firstValueFrom(
        this.appService.doGet(
          withQueryParams(this.apiUrl.apiUrl.onboarding.encryptRef, { applicationNumber }),
        ),
      );

      if (response.success && response.data) {
        ref = readApiString(response.data as unknown as Record<string, unknown>, 'Ref');
      }
    } catch {
      ref = '';
    }

    if (!ref) {
      ref = encodeURIComponent(applicationNumber);
    }

    this.onboardingState.setEncryptedAppRef(ref);
    this.onboardingState.setApplicationNumber(applicationNumber);
    return ref;
  }

  getEncryptedRef(): string | null {
    return this.onboardingState.getEncryptedAppRef();
  }

  link(): string[] {
    const ref = this.getEncryptedRef();
    return ref ? ['/onboarding', ref] : ['/onboarding'];
  }

  restoreStep(): void {
    const saved = this.storage.get<OnboardingPath>(this.stepKey);
    if (saved && this.isOnboardingPath(saved)) {
      this.currentStep.set(saved);
    }
  }

  resetSession(): void {
    this.storage.remove(this.stepKey);
    this.storage.remove(this.returnStepKey);
    this.currentStep.set(ONBOARDING_PATHS.accountType);
  }

  goToStep(step: OnboardingPath, options?: { returnTo?: OnboardingPath }): void {
    if (options?.returnTo) {
      this.storage.set(this.returnStepKey, options.returnTo);
    } else {
      this.clearReturnStep();
    }

    this.currentStep.set(step);
    this.storage.set(this.stepKey, step);
  }

  goToYourDetails(returnTo: OnboardingPath): void {
    this.goToStep(ONBOARDING_PATHS.yourDetails, { returnTo });
  }

  goToStepForEdit(step: OnboardingPath, returnTo: OnboardingPath): void {
    this.goToStep(step, { returnTo });
  }

  getReturnStep(): OnboardingPath | null {
    const saved = this.storage.get<OnboardingPath>(this.returnStepKey);
    return saved && this.isOnboardingPath(saved) ? saved : null;
  }

  isEditingFrom(step: OnboardingPath): boolean {
    return this.getReturnStep() === step;
  }

  completeStepEdit(fallbackStep: OnboardingPath): void {
    const returnTo = this.getReturnStep();
    this.clearReturnStep();
    this.goToStep(returnTo ?? fallbackStep);
  }

  cancelEdit(): void {
    const returnTo = this.getReturnStep();
    if (!returnTo) {
      return;
    }

    this.clearReturnStep();
    this.goToStep(returnTo);
  }

  completeYourDetails(fallbackStep: OnboardingPath): void {
    this.completeStepEdit(fallbackStep);
  }

  async enterOnboarding(
    router: Router,
    step: OnboardingPath = ONBOARDING_PATHS.accountType,
    encryptedRef?: string,
  ): Promise<boolean> {
    this.goToStep(step);
    const ref = encryptedRef ?? this.getEncryptedRef();
    if (!ref) {
      return false;
    }

    return router.navigate(['/onboarding', ref]);
  }

  async navigate(router: Router, step: OnboardingPath): Promise<boolean> {
    const ref = this.getEncryptedRef();
    if (!ref) {
      return false;
    }

    this.goToStep(step);

    if (this.isOnOnboardingFlow(ref)) {
      return true;
    }

    return router.navigate(['/onboarding', ref]);
  }

  async resolveApplicationNumberFromRef(appRef: string): Promise<string | null> {
    try {
      const response = await firstValueFrom(
        this.appService.doGet(
          withQueryParams(this.apiUrl.apiUrl.onboarding.resolveRef, { ref: appRef }),
        ),
      );

      if (!response.success || !response.data) {
        return null;
      }

      const applicationNumber = readApiString(
        response.data as unknown as Record<string, unknown>,
        'ApplicationNumber',
      );
      return applicationNumber || null;
    } catch {
      return null;
    }
  }

  // NEW (disabled, API-side flag version): same lookup as resolveApplicationNumberFromRef, but also returns
  // whether the ref came from the emailed link (StartFirst, carried inside the encrypted ref).
  // Needs `readApiBool` in the api-response.util import and the API "NEW (disabled)" blocks.
  // async resolveRefInfo(
  //   appRef: string,
  // ): Promise<{ applicationNumber: string; startFirst: boolean } | null> {
  //   try {
  //     const response = await firstValueFrom(
  //       this.appService.doGet(
  //         withQueryParams(this.apiUrl.apiUrl.onboarding.resolveRef, { ref: appRef }),
  //       ),
  //     );
  //
  //     if (!response.success || !response.data) {
  //       return null;
  //     }
  //
  //     const data = response.data as unknown as Record<string, unknown>;
  //     const applicationNumber = readApiString(data, 'ApplicationNumber');
  //     if (!applicationNumber) {
  //       return null;
  //     }
  //
  //     return { applicationNumber, startFirst: readApiBool(data, 'StartFirst') };
  //   } catch {
  //     return null;
  //   }
  // }

  isOnboardingPath(value: string): value is OnboardingPath {
    return Object.values(ONBOARDING_PATHS).includes(value as OnboardingPath);
  }

  private isOnOnboardingFlow(ref: string): boolean {
    const url = this.router.url.split('?')[0]?.split('#')[0] ?? '';
    return url === `/onboarding/${ref}`;
  }

  private clearReturnStep(): void {
    this.storage.remove(this.returnStepKey);
  }
}
